/**
 * MarketplacePublishSection
 *
 * Shows the current Marketplace publish state and provides
 * publish/unpublish/sync/retry actions.
 *
 * Renders inside the Portfolio page's right-column settings area.
 */

import { useState } from 'react';
import {
  useGetMarketplacePublishStatusQuery,
  usePublishToMarketplaceMutation,
  useUnpublishFromMarketplaceMutation,
  useSyncToMarketplaceMutation,
} from '../../store/api/portfolioApi';
import toast from 'react-hot-toast';

export function MarketplacePublishSection() {
  const { data: publishState, isLoading } = useGetMarketplacePublishStatusQuery();
  const [publishToMarketplace, { isLoading: isPublishing }] = usePublishToMarketplaceMutation();
  const [unpublishFromMarketplace, { isLoading: isUnpublishing }] = useUnpublishFromMarketplaceMutation();
  const [syncToMarketplace, { isLoading: isSyncing }] = useSyncToMarketplaceMutation();
  const [showConfirmUnpublish, setShowConfirmUnpublish] = useState(false);

  const status = publishState?.marketplaceStatus || 'unpublished';
  const isActionInProgress = isPublishing || isUnpublishing || isSyncing;

  const handlePublish = async () => {
    try {
      await publishToMarketplace().unwrap();
      toast.success('Published to Marketplace');
    } catch {
      toast.error('Failed to publish to Marketplace');
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishFromMarketplace().unwrap();
      setShowConfirmUnpublish(false);
      toast.success('Removed from Marketplace');
    } catch {
      toast.error('Failed to unpublish from Marketplace');
    }
  };

  const handleSync = async () => {
    try {
      await syncToMarketplace().unwrap();
      toast.success('Marketplace profile updated');
    } catch {
      toast.error('Failed to sync to Marketplace');
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Marketplace</h2>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-8 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-gray-900">Marketplace</h2>
      </div>
      <div className="card-body space-y-4">
        {/* Status indicator */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Status</p>
            <p className="text-xs text-gray-500">
              {status === 'published'
                ? 'Your trust signals are live on Marketplace'
                : status === 'sync_error'
                ? 'Last sync encountered an error'
                : 'Not yet published to Marketplace'}
            </p>
          </div>
          <StatusBadge status={status} />
        </div>

        {/* Sync error detail */}
        {status === 'sync_error' && publishState?.syncError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700">{publishState.syncError}</p>
          </div>
        )}

        {/* Last synced timestamp */}
        {publishState?.lastSyncedAt && (
          <p className="text-xs text-gray-400">
            Last synced: {new Date(publishState.lastSyncedAt).toLocaleString()}
          </p>
        )}

        {/* Actions based on state */}
        <div className="space-y-2">
          {status === 'unpublished' && (
            <button
              onClick={handlePublish}
              disabled={isActionInProgress}
              className="btn btn-primary w-full"
            >
              {isPublishing ? 'Publishing...' : 'Publish to Marketplace'}
            </button>
          )}

          {status === 'published' && (
            <>
              <button
                onClick={handleSync}
                disabled={isActionInProgress}
                className="btn btn-outline w-full"
              >
                {isSyncing ? 'Updating...' : 'Update Marketplace'}
              </button>
              {showConfirmUnpublish ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleUnpublish}
                    disabled={isActionInProgress}
                    className="btn flex-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {isUnpublishing ? 'Removing...' : 'Confirm Remove'}
                  </button>
                  <button
                    onClick={() => setShowConfirmUnpublish(false)}
                    className="btn btn-outline flex-1"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirmUnpublish(true)}
                  disabled={isActionInProgress}
                  className="btn btn-outline w-full text-red-600 border-red-300 hover:bg-red-50"
                >
                  Unpublish
                </button>
              )}
            </>
          )}

          {status === 'sync_error' && (
            <>
              <button
                onClick={handleSync}
                disabled={isActionInProgress}
                className="btn btn-primary w-full"
              >
                {isSyncing ? 'Retrying...' : 'Retry'}
              </button>
              <button
                onClick={handleUnpublish}
                disabled={isActionInProgress}
                className="btn btn-outline w-full text-red-600 border-red-300 hover:bg-red-50"
              >
                {isUnpublishing ? 'Removing...' : 'Unpublish'}
              </button>
            </>
          )}
        </div>

        {/* View on Marketplace link */}
        {status === 'published' && publishState?.marketplaceUrl && (
          <a
            href={publishState.marketplaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium pt-2"
          >
            View on Marketplace
            <ExternalLinkIcon className="w-3.5 h-3.5" />
          </a>
        )}

        {/* What gets published */}
        {status === 'unpublished' && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-gray-500 mb-2">What gets published:</p>
            <ul className="space-y-1 text-xs text-gray-500">
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-3 h-3 text-green-500 flex-shrink-0" />
                Verified roles and authority level
              </li>
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-3 h-3 text-green-500 flex-shrink-0" />
                Verified credentials
              </li>
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-3 h-3 text-green-500 flex-shrink-0" />
                Featured sample calls (top 3)
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = {
    published: { label: 'Published', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
    sync_error: { label: 'Error', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
    unpublished: { label: 'Unpublished', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  }[status] || { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
